import React from 'react';
import _ from 'underscore';

import useOnMount from 'ui-library/hooks/useOnMount';
import LegacyTagsInput from 'ui-library/tags-input/LegacyTagsInput';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {isCommaKey, isEnterKey, isSemicolonKey, isTabKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

// email_regex from: http://stackoverflow.com/questions/46155/validate-email-address-in-javascript
const EMAIL_REGEX =
  /(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})/gi;

/**
 * Extracts all email addresses from a given text input and returns them in an array.
 * @package
 */
export function extractEmailAddresses(text: string): Array<string> {
  return [...text.matchAll(EMAIL_REGEX)].map((match) => match[0]);
}

export interface EmailInputProps {
  emails: Array<string>;
  onChangeEmails: (emails: Array<string>) => void;
  size: TagsInputSize;
  focusOnMount: boolean;
}

export default function EmailInput({
  emails,
  onChangeEmails,
  size,
  focusOnMount,
}: EmailInputProps): JSX.Element {
  const inputRef = React.useRef<LegacyTagsInput>(null);

  useOnMount(() => {
    if (focusOnMount && inputRef.current) {
      inputRef.current.focus();
    }
  });

  return (
    <div className="u-flex-grow">
      <LegacyTagsInput
        // Add an option on tab, enter, semicolon, or comma
        addPredicate={(e: ElementKeyboardEvent) =>
          isTabKey(e) || isEnterKey(e) || isSemicolonKey(e) || isCommaKey(e)
        }
        onChange={(options: Array<{text: string}>): void => {
          onChangeEmails(_.unique(options.flatMap((option) => extractEmailAddresses(option.text))));
        }}
        placeholder="Enter email addresses"
        size={size}
        value={emails.map((email) => ({id: email, text: email}))}
        ref={inputRef}
      />
    </div>
  );
}
